import { Button, Card, SvgIcon } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { RiAddLine } from '@remixicon/react';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { MainSectionTemplate } from '../../../components/AdminTemplates';
import CustomDataGrid from '../../../components/CustomDataGrid';
import { useCurrentUser } from '../../../contexts/currentUser';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import useQueryErrorNotifier from '../../../hooks/useQueryErrorNotifier';
import { InvoiceStatus } from '../../../types/invoices';
import { MONTHS } from '../../../utils/date';
import InvoiceValidationDialog from './InvoiceValidationDialog';
import StatusBadge from './StatusBadge';
import AdminTableDetailLink from '../../../components/AdminTableDetailLink';
import { useDataGridSearchParamsFiltersManager } from '../../../hooks/useSearchParamsFilters';

interface InvoiceDisplayedItem {
  id: string;
  invoiceNumber: string;
  monthYear: string;
  operatorName: string;
  contract: string;
  status: InvoiceStatus;
}

const InvoicesPage = () => {
  const { t } = useLanguage();
  const [
    displayingInvoiceValidationDialog,
    setDisplayingInvoiceValidationDialog,
  ] = useState(false);

  const { isOperator } = useCurrentUser();
  const { getInvoices } = useIotsApi();

  const {
    data: fetchedInvoices = [],
    isFetching,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['invoices'],
    queryFn: () => getInvoices(),
  });

  useQueryErrorNotifier([error]);

  const { initialFilterModel, onFilterModelChange } =
    useDataGridSearchParamsFiltersManager();

  const { invoices, monthYears, monthYearsMap } = useMemo(() => {
    const invoices: InvoiceDisplayedItem[] = [];
    const monthYears: string[] = [];
    const monthYearsMap: Record<string, string> = {};

    fetchedInvoices.forEach(invoice => {
      const monthYear = `${invoice.year}-${invoice.month.toString().padStart(2, '0')}`;
      const invoiceDisplayedItem: InvoiceDisplayedItem = {
        id: invoice.id,
        invoiceNumber: invoice.invoiceNumber,
        monthYear,
        operatorName: invoice.operatorName,
        contract: invoice.contractName,
        status: invoice.status,
      };

      if (!monthYearsMap[monthYear]) {
        monthYearsMap[monthYear] =
          `${t(`months.${MONTHS[invoice.month - 1]}`)} ${invoice.year}`;
        monthYears.push(monthYear);
      }

      invoices.push(invoiceDisplayedItem);
    });

    monthYears.sort((a, b) => b.localeCompare(a));

    return { invoices, monthYears, monthYearsMap };
  }, [fetchedInvoices, t]);

  const columns = useMemo<GridColDef<InvoiceDisplayedItem>[]>(
    () => [
      {
        field: 'monthYear',
        flex: 2,
        minWidth: 140,
        headerName: t('content.month'),
        valueFormatter: (monthYear: string) => monthYearsMap[monthYear] ?? '',
        type: 'singleSelect',
        valueOptions: monthYears.map(monthYear => ({
          value: monthYear,
          label: monthYearsMap[monthYear],
        })),
      },
      {
        field: 'contract',
        flex: 3,
        minWidth: 200,
        headerName: t('content.contract'),
      },
      ...(!isOperator
        ? [
            {
              field: 'operatorName',
              minWidth: 200,
              flex: 3,
              headerName: t('content.operator'),
            },
          ]
        : []),
      {
        field: 'invoiceNumber',
        flex: 3,
        headerName: t('content.invoice-number'),
        minWidth: 180,
      },
      {
        field: 'status',
        display: 'flex',
        minWidth: 160,
        headerName: t('content.status'),
        type: 'singleSelect',
        valueOptions: Object.values(InvoiceStatus).map(status => ({
          value: status,
          label: t(`invoice-status.${status}`),
        })),
        renderCell: params => <StatusBadge status={params.row.status} small />,
      },
      {
        field: '',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
        hideable: false,
        display: 'flex',
        renderCell: params => (
          <AdminTableDetailLink
            isLoading={isFetching}
            href={`/admin/invoices/${params.row.id}`}
          />
        ),
      },
    ],
    [isFetching, isOperator, monthYears, monthYearsMap, t],
  );

  return (
    <MainSectionTemplate
      title={t('content.invoices')}
      actions={
        <Button
          startIcon={<SvgIcon component={RiAddLine} fontSize="small" />}
          size="small"
          variant="contained"
          onClick={() => {
            setDisplayingInvoiceValidationDialog(true);
          }}
        >
          {t('content.add-new')}
        </Button>
      }
    >
      <Card variant="outlined">
        <CustomDataGrid
          rows={invoices}
          loading={isLoading}
          columns={columns}
          onFilterModelChange={onFilterModelChange}
          initialState={{
            filter: {
              filterModel: initialFilterModel,
            },
            sorting: {
              sortModel: [
                {
                  field: 'monthYear',
                  sort: 'desc',
                },
              ],
            },
          }}
        />
      </Card>
      <InvoiceValidationDialog
        isOpen={displayingInvoiceValidationDialog}
        onClose={() => {
          setDisplayingInvoiceValidationDialog(false);
        }}
      />
    </MainSectionTemplate>
  );
};

export default InvoicesPage;
